import { ChangeEventHandler, useState, useRef, useCallback } from "react";
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import { Button } from "@/components/ui/button.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { toast } from "@/components/ui/use-toast.ts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { User } from "@/types/User.ts";
import { UserRole } from "@/types/UserRole.ts";
import { uploadUserProfileImage } from "@/services/userService.ts";
import { getTimeAgoString } from "@/utils/time-helper.ts";
import { fileToDataString, getImgSrc, getOriginalProfilePictureSrc } from "@/utils/file-upload.ts";
import { getCroppedImageBlob, blobToFile } from "@/utils/crop-image.ts";
import { useTranslation } from "react-i18next";
import { Loader2, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.tsx";

function getInitials(fullName: string | undefined): string {
  if (!fullName?.trim()) return "?";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export const ProfileLeftPanel = ({
  user,
  onProfilePictureUpdated,
  variant = "sidebar",
}: {
  user: any;
  onProfilePictureUpdated?: () => void | Promise<void>;
  variant?: "sidebar" | "compact";
}) => {
  const { t } = useTranslation();
  const [imageToCrop, setImageToCrop] = useState<string>("");
  const [crop, setCrop] = useState<Crop>();
  const [uploading, setUploading] = useState(false);
  const [imageLoadedInCrop, setImageLoadedInCrop] = useState(false);
  const [loadingBeforeCropDialog, setLoadingBeforeCropDialog] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** When user selects a new photo (before crop), we keep the original file to upload alongside the cropped one. */
  const originalFileRef = useRef<File | null>(null);
  /** Skip img onError toast when we're closing the dialog (Cancel / Use this photo) and clear src. */
  const isClosingCropRef = useRef(false);
  const { loggedInUser, setLoggedInUser } = useAuth();
  const isAdmin = (loggedInUser && loggedInUser.role === UserRole.ADMIN) || false;
  const hasProfilePicture = Boolean(user?.profilePictureSrc);

  const onRequestError = (error: any) => {
    toast({
      variant: "destructive",
      title: t("common.errorTitle"),
      description: error?.message ?? t("common.errorDesc"),
    });
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
    setImageLoadedInCrop(true);
  }, []);

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    originalFileRef.current = file;
    setLoadingBeforeCropDialog(true);
    try {
      isClosingCropRef.current = false;
      setImageLoadedInCrop(false);
      const imgUrl = await fileToDataString(file);
      setImageToCrop(imgUrl);
    } catch (error) {
      onRequestError(error);
      originalFileRef.current = null;
    } finally {
      setLoadingBeforeCropDialog(false);
    }
  };

  const handleUsePhoto = async () => {
    if (!imgRef.current || !crop?.width || !crop?.height) return;
    setUploading(true);
    const originalFile = originalFileRef.current;
    try {
      const blob = await getCroppedImageBlob(imgRef.current, crop, 400);
      const file = blobToFile(blob, "avatar.jpg");
      const updatedUser = await uploadUserProfileImage(user.id, file, originalFile ?? undefined);
      originalFileRef.current = null;
      isClosingCropRef.current = true;
      setImageToCrop("");
      if (loggedInUser?.id === user.id && updatedUser?.profilePictureSrc != null) {
        setLoggedInUser({
          ...loggedInUser,
          profilePictureSrc: updatedUser.profilePictureSrc,
        } as User);
      }
      toast({
        variant: "success",
        title: t("common.successTitle"),
        description: t("profile.uploadPhotoSuccessDesc"),
      });
      await onProfilePictureUpdated?.();
    } catch (error) {
      onRequestError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseCrop = () => {
    if (!uploading) {
      isClosingCropRef.current = true;
      if (imageToCrop.startsWith("blob:")) {
        URL.revokeObjectURL(imageToCrop);
      }
      originalFileRef.current = null;
      setImageToCrop("");
    }
  };

  const openRepositionPhoto = () => {
    isClosingCropRef.current = false;
    setImageLoadedInCrop(false);
    const originalSrc = getOriginalProfilePictureSrc(user.profilePictureSrc);
    const url = getImgSrc(originalSrc) ?? getImgSrc(user.profilePictureSrc);
    if (!url) return;
    setImageToCrop(url);
  };

  const showCropDialog = Boolean(imageToCrop);
  const canEditPhoto = Boolean(loggedInUser && (isAdmin || loggedInUser?.id === user.id));
  const avatarSrc = getImgSrc(user.profilePictureSrc) ?? import.meta.env.VITE_DEFAULT_AVATAR ?? null;
  const showPlaceholder = !avatarSrc;

  const avatarSizeClasses = variant === "compact" ? "w-20 h-20 shrink-0 sm:w-24 sm:h-24" : "w-full aspect-square";
  const avatarRoundClass = variant === "compact" ? "rounded-full" : "rounded-xl";

  const avatarContent = showPlaceholder ? (
    <div
      className={`${avatarSizeClasses} ${avatarRoundClass} flex items-center justify-center bg-bg-elevated border border-border text-text-2 font-semibold select-none ${variant === "compact" ? "text-2xl sm:text-3xl" : "text-4xl"}`}
      aria-hidden
    >
      {user?.fullName ? getInitials(user.fullName) : <UserIcon className={variant === "compact" ? "h-10 w-10 sm:h-12 sm:w-12" : "h-16 w-16"} />}
    </div>
  ) : (
    <img
      className={`${avatarSizeClasses} ${avatarRoundClass} object-cover bg-bg-elevated`}
      alt={t("profile.altText")}
      src={avatarSrc ?? undefined}
    />
  );

  const editOverlay = canEditPhoto && (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-black/50 opacity-0 transition-opacity duration-200 hover:opacity-100 focus-within:opacity-100">
      {hasProfilePicture ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-white font-semibold text-charcoal shadow-sm hover:bg-gray-100 dark:bg-charcoal dark:text-white dark:hover:bg-gray-800"
            >
              {t("profile.updatePhoto")}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="min-w-[12rem]">
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                fileInputRef.current?.click();
              }}
            >
              {t("profile.uploadNewPhoto")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                openRepositionPhoto();
              }}
            >
              {t("profile.repositionPhoto")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-white font-semibold text-charcoal shadow-sm hover:bg-gray-100 dark:bg-charcoal dark:text-white dark:hover:bg-gray-800"
          aria-label={t("profile.updatePhoto")}
        >
          {t("profile.updatePhoto")}
        </button>
      )}
    </div>
  );

  const avatarBlock = (
    <div className={`relative ${variant === "compact" ? "rounded-full" : "rounded-xl overflow-hidden"}`}>
      {loadingBeforeCropDialog && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/50 rounded-[inherit]">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
          <span className="text-sm text-white/90">{t("common.loading")}</span>
        </div>
      )}
      {avatarContent}
      {canEditPhoto && editOverlay}
      {canEditPhoto && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          aria-hidden
        />
      )}
    </div>
  );

  const metaBlock = (
    <>
      <h1 className="font-bold text-xl leading-8 my-1 text-text-1">{user.fullName}</h1>
      <p className="text-sm text-text-2 leading-6">{user.email}</p>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Badge
          variant={
            user.role === UserRole.ADMIN
              ? "destructive"
              : user.role === UserRole.MENTOR
                ? "secondary"
                : "default"
          }
          className="uppercase"
        >
          {user.role}
        </Badge>
        <span className="text-xs text-text-3">{t("profile.lastActive")} {getTimeAgoString(user.lastLoggedIn, t)}</span>
      </div>
    </>
  );

  if (variant === "compact") {
    return (
      <div className="w-full min-w-0">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 p-4 rounded-xl border border-border bg-bg-card shadow-sm">
          {avatarBlock}
          <div className="min-w-0 flex-1">
            {metaBlock}
          </div>
        </div>
        <Dialog open={showCropDialog} onOpenChange={(open) => !open && handleCloseCrop()}>
          <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => uploading && e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{t("profile.cropPhotoTitle")}</DialogTitle>
              <DialogDescription>{t("profile.cropPhotoDesc")}</DialogDescription>
            </DialogHeader>
            <div className="relative max-h-[60vh] overflow-auto flex justify-center bg-muted/30 rounded-md py-2 min-h-[200px]">
              {!imageLoadedInCrop && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-muted/50 rounded-md">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">{t("common.loading")}</span>
                </div>
              )}
              <ReactCrop
                crop={crop}
                onChange={(_c, percentCrop) => setCrop(percentCrop)}
                aspect={1}
                circularCrop
                className="max-w-full"
              >
                <img
                  ref={imgRef}
                  src={imageToCrop}
                  alt="Crop"
                  style={{ maxHeight: "60vh", width: "auto" }}
                  onLoad={onImageLoad}
                  onError={() => {
                    if (isClosingCropRef.current) {
                      isClosingCropRef.current = false;
                      return;
                    }
                    setImageToCrop("");
                    onRequestError(new Error(t("profile.repositionLoadError")));
                  }}
                />
              </ReactCrop>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseCrop} disabled={uploading}>
                {t("common.cancel")}
              </Button>
              <Button type="button" onClick={handleUsePhoto} disabled={uploading || !crop?.width || !crop?.height}>
                {uploading ? t("common.processing") : t("profile.useThisPhoto")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      <div className="p-4 rounded-xl border border-border bg-bg-card shadow-sm">
        <div className="overflow-hidden rounded-xl">
          {avatarBlock}
        </div>
        <div className="mt-4">
          {metaBlock}
        </div>
      </div>

      <Dialog open={showCropDialog} onOpenChange={(open) => !open && handleCloseCrop()}>
        <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => uploading && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t("profile.cropPhotoTitle")}</DialogTitle>
            <DialogDescription>{t("profile.cropPhotoDesc")}</DialogDescription>
          </DialogHeader>
          <div className="relative max-h-[60vh] overflow-auto flex justify-center bg-muted/30 rounded-md py-2 min-h-[200px]">
            {!imageLoadedInCrop && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-muted/50 rounded-md">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">{t("common.loading")}</span>
              </div>
            )}
            <ReactCrop
              crop={crop}
              onChange={(_c, percentCrop) => setCrop(percentCrop)}
              aspect={1}
              circularCrop
              className="max-w-full"
            >
              <img
                ref={imgRef}
                src={imageToCrop}
                alt="Crop"
                style={{ maxHeight: "60vh", width: "auto" }}
                onLoad={onImageLoad}
                onError={() => {
                  if (isClosingCropRef.current) {
                    isClosingCropRef.current = false;
                    return;
                  }
                  setImageToCrop("");
                  onRequestError(new Error(t("profile.repositionLoadError")));
                }}
              />
            </ReactCrop>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseCrop}
              disabled={uploading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleUsePhoto}
              disabled={uploading || !crop?.width || !crop?.height}
            >
              {uploading ? t("common.processing") : t("profile.useThisPhoto")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
